// Single source of truth for all Salamis Bay Conti hotel facts.
// All schema generators should import from here — never hard-code hotel data elsewhere.

export const HOTEL = {
  SITE_URL: 'https://salamisbayconti.com',

  officialName: 'Salamis Bay Conti Resort Hotel',
  alternateName: ['Salamis Bay Conti', 'Salamis Hotel'],

  url: 'https://salamisbayconti.com',
  // public/og-image.jpg — 1200x630 crop of the homepage hero photo. Served
  // with a 1-year immutable cache ('/**/*.jpg' route rule), so a new image
  // needs a new filename, not an overwrite.
  ogImage: 'https://salamisbayconti.com/og-image.jpg',
  favicon: 'https://salamisbayconti.com/favicon.ico',

  address: {
    streetAddressTR: 'Salamis Bay Conti Resort Hotel & Casino, Yeni Boğaziçi',
    streetAddressEN: 'Salamis Bay Conti Resort Hotel & Casino, Yeni Bogazici',
    addressLocalityTR: 'Gazimağusa',
    addressLocalityEN: 'Famagusta',
    postalCode: '99680',
    addressCountry: 'CY',
    regionEN: 'North Cyprus',
    regionTR: 'Kuzey Kıbrıs'
  },

  geo: {
    latitude: 35.205808,
    longitude: 33.899638
  },

  contact: {
    phoneMain: '+908507070101',       // Toll-free international (+90 850 707 01 01)
    phoneReception: '+903926330000',  // Local reception (+90 392 633 00 00)
    phoneWhatsApp: '+905469999333',   // WhatsApp (+90 546 999 93 33)
    email: 'onlinecc@salamisbayconti.com',
    whatsappUrl: 'https://api.whatsapp.com/send?phone=905469999333'
  },

  rooms: {
    total: 664,
    mainBuilding: 293,
    salamisPark: 110,
    salamisGarden: 151,
    standardVillas: 13,
    juniorVillas: 10,
    duplexVillas: 21,
    connectedRooms: 54,
    suites: 8,
    accessibleRooms: 4
  },

  rating: {
    stars: 5,
    value: '5',
    bestRating: '5',
    // Third-party review rating, shown only as attributed visible text
    // (components/Rewards.vue). Deliberately NOT emitted as JSON-LD
    // `aggregateRating`: Google's review-snippet rules exclude ratings
    // aggregated from another site and self-serving ratings on the
    // business's own Hotel/LocalBusiness entity.
    // Source: HolidayCheck (6-point scale) — confirmed against
    // https://www.holidaycheck.de/hi/salamis-bay-conti-resort-hotel-casino/0787cbc0-a97f-3532-956e-725a77d2055b
    // on 2026-09-09. Re-verify periodically; HolidayCheck's own number
    // moves as new reviews come in.
    aggregate: { ratingValue: '5.6', reviewCount: '2523', bestRating: '6' }
  },

  priceRange: '€€€',
  checkinTime: 'T14:00',
  checkoutTime: 'T12:00',
  landArea: '65000',  // m²

  amenities: [
    { name: 'Private Beach',              value: true },
    { name: 'Lemon Spa & Wellness',       value: true },
    { name: 'Fitness Center',             value: true },
    { name: 'Swimming Pool',              value: true },
    { name: 'Ultra All-Inclusive',        value: true },
    { name: 'Multiple Restaurants & Bars',value: true },
    { name: 'Conference & Meeting Rooms', value: true },
    { name: 'Beauty Salon',               value: true },
    { name: 'Free Wi-Fi',                 value: true },
    { name: 'Interconnected Family Rooms',value: true },
    { name: 'Accessible Rooms',           value: true }
  ],

  // 12 F&B venues (from sitemap routes)
  restaurants: [
    { name: 'Chinese Restaurant',  slug: 'chinese-restaurant' },
    { name: 'Italian Restaurant',  slug: 'italian-restaurant' },
    { name: 'Selçuk Restaurant',   slug: 'selcuk-restaurant' },
    { name: 'Othello Bar',         slug: 'othello-bar' },
    { name: 'Cocktail Bar',        slug: 'coctail-bar' },
    { name: 'Patio Cafe',          slug: 'patio-cafe' },
    { name: 'Pier Bar',            slug: 'pier-bar' },
    { name: 'Lobby Bar',           slug: 'lobby-bar' },
    { name: 'Pool Bar',            slug: 'pool-bar' },
    { name: 'Beach Bar',           slug: 'beach-bar' },
    { name: 'Saluna Patisserie',   slug: 'saluna-patisserie' },
    { name: 'Corner Pub',          slug: 'corner-pub' }
  ],

  // 9 meeting & conference halls (from sitemap routes)
  meetingHalls: [
    { name: 'Salamis Salloon',    slug: 'salamis-salloon' },
    { name: 'Namık Kemal',        slug: 'namik-kemal' },
    { name: 'Othello',            slug: 'othello' },
    { name: 'Lala Mustafa Paşa',  slug: 'lala-mustafa-pasa' },
    { name: 'Kantara',            slug: 'kantara' },
    { name: 'Fuaye',              slug: 'fuaye' },
    { name: 'Canbulat',           slug: 'canbulat' },
    { name: 'Akkule',             slug: 'akkule' },
    { name: 'Barnabas',           slug: 'barnabas' }
  ],

  // Room types (from sitemap routes)
  roomTypes: [
    { name: 'Standard Sea View Room', slug: 'standart-sea-view-room' },
    { name: 'Suite Room',             slug: 'suit-room' },
    { name: 'Park Standard Room',     slug: 'park-standart-room' },
    { name: 'Standard Villa',         slug: 'standart-villa' },
    { name: 'Garden Room',            slug: 'garden-room' },
    { name: 'Duplex Villa',           slug: 'duplex-villa' },
    { name: 'Junior Villa',           slug: 'junior-villa' },
    { name: 'Seafront Room',          slug: 'seafront-room' }
  ],

  // Verified social + review-platform profiles. TripAdvisor/HolidayCheck
  // URLs were already live on-site (components/Rewards.vue, as award-badge
  // links) but never added here — meaning the site never formally vouched
  // for the very listings it cites 10 awards from. Reusing the exact URLs
  // already in Rewards.vue rather than re-deriving them.
  sameAs: [
    'https://www.facebook.com/SalamisBayContiResortHotel',
    'https://www.instagram.com/salamisbaycontiresort/',
    'https://www.youtube.com/channel/UCMT2lcf1B9A9DWMYwqVYnFw',
    'https://www.linkedin.com/company/salamis-bay-conti/',
    'https://www.tiktok.com/@salamisbaycontiresort',
    'https://www.tripadvisor.com.tr/Hotel_Review-g12934547-d651191-Reviews-Salamis_Bay_Conti_Resort_Hotel_Casino-Yeni_Bogazici_Famagusta_District.html',
    'https://www.holidaycheck.de/hi/salamis-bay-conti-resort-hotel-casino/0787cbc0-a97f-3532-956e-725a77d2055b',
    // Verified against booking.com's own listing (2,608 reviews) — added so
    // AI entity-resolution links the OTA listing that dominates citation
    // share back to this domain as the same real-world entity.
    'https://www.booking.com/hotel/xy/salamis-park-famagusta.html',
    // Created 2026-09-11 (Q141432230) — statements (P31/P856/P625/P17) not
    // yet filled in on the Wikidata side as of this writing; the entity
    // itself exists so the link is added here regardless.
    'https://www.wikidata.org/wiki/Q141432230'
  ],

  // Nearby attractions
  nearbyAttractions: [
    { name: 'Salamis Ancient City',  type: 'TouristAttraction' },
    { name: 'Famagusta Old City',    type: 'TouristAttraction' },
    { name: 'Othello Castle',        type: 'TouristAttraction' },
    { name: 'Lala Mustafa Pasha Mosque', type: 'LandmarksOrHistoricalBuildings' }
  ]
}
